import PropTypes from 'prop-types';

const MS_PER_DAY = 86400000;

/**
 * Computes how many days a deal has spent in each pipeline stage,
 * based on the backend stage history (Pipedrive-style "X days in stage").
 *
 * - Past stages: sum of `daysInPrevStage` across all transitions that left this
 *   stage (`fromStageId === stage.id`). Multiple passes are added up.
 * - Current stage: difference between now and the last entry (`movedAt` of the
 *   latest record with `toStageId === currentStageId`), falling back to `createdAt`.
 * - Future stages: no days (null).
 *
 * @param {Object} params
 * @param {Array} params.stages - Sorted pipeline stages ([{ id, name, stageOrder }])
 * @param {string} params.currentStageId - The deal's current stage
 * @param {Array} [params.stageHistory] - Stage transitions ([{ fromStageId, toStageId, movedAt, daysInPrevStage }])
 * @param {string} [params.createdAt] - Deal creation timestamp (fallback for the current stage)
 * @returns {Object<string, number|null>} Map stageId -> days (integer), or null for stages not yet reached
 */
const computeDaysPerStage = ({ stages, currentStageId, stageHistory = [], createdAt }) => {
	const currentIdx = stages.findIndex((s) => s.id === currentStageId);
	const result = {};

	for (const [idx, stage] of stages.entries()) {
		if (idx > currentIdx) {
			result[stage.id] = null;
			continue;
		}

		if (idx === currentIdx) {
			const lastEntry = stageHistory
				.filter((h) => h.toStageId === stage.id && h.movedAt)
				.sort((a, b) => new Date(b.movedAt) - new Date(a.movedAt))[0];
			const enteredAt = lastEntry?.movedAt ?? createdAt;
			result[stage.id] = enteredAt
				? Math.max(0, Math.floor((Date.now() - new Date(enteredAt).getTime()) / MS_PER_DAY))
				: 0;
			continue;
		}

		// Past stage: sum all durations spent in it before leaving
		result[stage.id] = stageHistory
			.filter((h) => h.fromStageId === stage.id)
			.reduce((sum, h) => sum + (Number(h.daysInPrevStage) || 0), 0);
	}

	return result;
};

// Formats a day count as a German label ("1 Tag" / "3 Tage").
const formatDayLabel = (days) => {
	const rounded = Math.round(days);
	return `${rounded} ${rounded === 1 ? 'Tag' : 'Tage'}`;
};

// Chevron clip-paths: middle segments point both ways, edges are flat on the outer side.
const CLIP_MIDDLE = 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)';
const CLIP_FIRST = 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)';
const CLIP_LAST = 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 10px 50%)';
const CLIP_ONLY = 'polygon(0 0, 100% 0, 100% 100%, 0 100%)';

const getClip = (i, count) => {
	if (count === 1) return CLIP_ONLY;
	if (i === 0) return CLIP_FIRST;
	if (i === count - 1) return CLIP_LAST;
	return CLIP_MIDDLE;
};

const getTipText = ({ act, done, dayLabel }) => {
	if (act) return `Seit ${dayLabel} hier`;
	if (done) return `${dayLabel} in dieser Phase`;
	return 'Noch nicht erreicht';
};

/**
 * Pipedrive-style stage timeline: chevron segments showing days per stage.
 * Brand-colored for completed + current stages, grey for future ones.
 * Hover reveals a tooltip with the stage name and time spent.
 */
const StageTimeline = ({ stages, currentStageId, stageHistory, createdAt, onTransition }) => {
	const currentIdx = stages.findIndex((s) => s.id === currentStageId);
	const daysByStage = computeDaysPerStage({ stages, currentStageId, stageHistory, createdAt });

	return (
		<div
			className="flex items-stretch gap-[3px] pt-4 pb-1.5 border-t border-bgBase-400"
			role="list"
			aria-label="Phasen-Verlauf"
		>
			{stages.map((s, i) => {
				const done = i < currentIdx;
				const act = i === currentIdx;
				const future = i > currentIdx;
				const days = daysByStage[s.id];
				const dayLabel = formatDayLabel(days ?? 0);
				const tipText = getTipText({ act, done, dayLabel });

				const bg = future
					? 'bg-bgBase-500 text-bgBase-800'
					: act
						? 'bg-primary-600 text-white'
						: 'bg-primary-500 text-white';

				return (
					<button
						key={s.id}
						type="button"
						role="listitem"
						onClick={() => onTransition(s.id)}
						className="group relative flex-1 min-w-0 h-8 p-0 border-none bg-transparent cursor-pointer"
						title={`${s.name} — ${tipText}`}
					>
						<span
							className={`absolute inset-0 flex items-center justify-center px-3.5 text-xs font-semibold whitespace-nowrap overflow-hidden text-ellipsis transition-[filter,background] duration-150 group-hover:brightness-105 ${bg}`}
							style={{ clipPath: getClip(i, stages.length) }}
						>
							{future ? (
								<span className="text-[11.5px] font-medium overflow-hidden text-ellipsis">
									{s.name}
								</span>
							) : (
								<span className="tabular-nums">{dayLabel}</span>
							)}
						</span>

						<span
							role="tooltip"
							className="pointer-events-none absolute left-1/2 top-[calc(100%+8px)] z-20 flex -translate-x-1/2 translate-y-[-4px] flex-col gap-px whitespace-nowrap rounded-control bg-text px-2.5 py-1.5 text-xs leading-snug text-white opacity-0 shadow-elevated transition-[opacity,transform] duration-150 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100"
						>
							<span className="absolute left-1/2 top-[-4px] h-2 w-2 -translate-x-1/2 rotate-45 bg-text" />
							<span className="font-semibold">{s.name}</span>
							<span className="text-[11.5px] font-normal text-bgBase-600">{tipText}</span>
						</span>
					</button>
				);
			})}
		</div>
	);
};

StageTimeline.propTypes = {
	stages: PropTypes.array,
	currentStageId: PropTypes.string,
	stageHistory: PropTypes.array,
	createdAt: PropTypes.string,
	onTransition: PropTypes.func,
};

StageTimeline.defaultProps = {
	stages: [],
	stageHistory: [],
};

export default StageTimeline;
